import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { PaHttpClient } from 'src/app/clients/http-client';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ToastrService } from 'src/app/shared/services/toastr.service';
import { GmapService } from 'src/app/shared/services/gmap.service';
import { AssignSpPopupComponent, AssignType } from 'src/app/views/requests/detail/assign-sp-popup/assign-sp-popup.component';
import { ContactListPopupComponent } from 'src/app/shared/components/contact-list-popup/contact-list-popup.component';
import { AddContactPopupComponent } from 'src/app/views/contacts/add-contact-popup/add-contact-popup.component';
import { ActivityRefreshService } from 'src/app/shared/services/activity-refresh.service';

declare var google: any;

type AmenityStep =
  | 'who_for'
  | 'amenity_type'
  | 'select_contacts'
  | 'pickup_datetime'
  | 'time_slots'
  | 'review_details';

interface ServiceType {
  id: string;
  name: string;
  sub_title: string | null;
  url: string | null;
  service_type_id?: string;
  key?: string | null;
}

interface Slot {
  id: string;
  time: string;
  isMorning: boolean;
  is_available: boolean;
}

function getDefaultPickupDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

@Component({
  selector: 'pa-amenities-create',
  imports: [CommonModule, DialogModule, AssignSpPopupComponent, ContactListPopupComponent, AddContactPopupComponent],
  templateUrl: './amenities-create.component.html',
  styleUrls: ['./amenities-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PaHttpClient],
  host: {
    '(document:click)': 'onDocClick($event)',
    '(window:resize)': 'onWindowResize()',
    '(window:orientationchange)': 'onWindowResize()',
  },
})
export class AmenitiesCreateComponent implements OnInit {
  close = output<void>();
  requestComplete = output<any>();

  private readonly httpClient = inject(PaHttpClient);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly gmapService = inject(GmapService);
  private readonly activityRefresh = inject(ActivityRefreshService);

  readonly currentUser = this.authService.getUser();

  // ── Navigation ─────────────────────────────────────────────
  readonly currentStep = signal<AmenityStep>('who_for');
  readonly isMobileView = signal(false);

  // ── Location dialog ─────────────────────────────────────────
  readonly showLocationDialog = signal(false);
  readonly locationAddress = signal('');
  readonly addressInput = signal('');
  readonly autocompleteResults = signal<any[]>([]);
  readonly locationSearchCenter = signal<{ lat: number; lng: number } | null>(null);

  @ViewChild('locationMapEl') private locationMapEl?: ElementRef<HTMLDivElement>;
  private locationMapInstance: any = null;
  private locationMapMarkers: any[] = [];
  private locationAutocompleteService: any = null;
  private locationMapInitialized = false;
  private locationPickupOverlay: any = null;

  readonly savedAddresses = computed<{ label: string }[]>(() => {
    const addrs: any[] = (this.currentUser as any)?.addresses ?? [];
    return addrs.map(a => ({
      label: [a.address, a.city, a.state?.short_name ?? a.state, a.zipcode].filter(Boolean).join(', '),
    }));
  });

  // ── Step 1: Who For ─────────────────────────────────────────
  readonly forSelf = signal<boolean | null>(null);

  // ── Step 2: Amenity Type ────────────────────────────────────
  readonly serviceTypes = signal<ServiceType[]>([]);
  readonly selectedServiceType = signal<ServiceType | null>(null);
  readonly isLoadingServiceTypes = signal(false);

  readonly iconMap: Record<string, string> = {
    laundry: 'laundry.svg',
    cleaning: 'cleaning.svg',
    pet_care: 'petcare.svg',
    handyman_moving: 'handyman.svg',
    photo_video: 'photo.svg',
    healthcare_homeaide: 'healthcare.svg',
    tutoring: 'tutoring.svg',
    personal_services: 'personal.svg',
    freelance_work: 'freelancer.svg',
    automotive_services: 'automotive.svg',
    event_services: 'catering.svg',
    health_beauty_services: 'beauty.svg',
    home_improvement: 'home-improvement.svg',
    business_services: 'business-service.svg',
    legal_services: 'legal.svg',
    yard_work: 'yard.svg',
  };

  // ── Step 3: Select Contacts ─────────────────────────────────
  readonly selectedGuests = signal<any[]>([]);
  readonly selectedFrontDesk = signal<any | null>(null);
  readonly selectedManagement = signal<any | null>(null);
  readonly returnToReview = signal(false);
  readonly isAddContactPopupOpen = signal(false);

  // ── Step 4: Pickup Date & Time ──────────────────────────────
  readonly selectedDate = signal<string>(getDefaultPickupDate());
  readonly selectedHour = signal<string>('09');
  readonly selectedMinute = signal<string>('00');
  readonly showHourDropdown = signal(false);
  readonly showMinuteDropdown = signal(false);

  readonly hoursList: { value: string; title: string }[] = Array.from({ length: 24 }, (_, x) => {
    const v = x < 10 ? '0' + x : String(x);
    return { value: v, title: `${v} (${x % 12 || 12} ${x < 12 ? 'AM' : 'PM'})` };
  });
  readonly minutesList: string[] = Array.from({ length: 60 }, (_, x) => String(x).padStart(2, '0'));

  // ── Step 5: Time Slots ──────────────────────────────────────
  readonly slots = signal<Slot[]>([]);
  readonly isLoadingSlots = signal(false);
  readonly selectedHoursCount = signal(1);
  readonly selectedSlots = signal<string[]>([]);
  readonly morningSlots = computed<Slot[]>(() => this.slots().filter(s => this.isSlotMorning(s)));
  readonly afternoonSlots = computed<Slot[]>(() => this.slots().filter(s => !this.isSlotMorning(s)));

  readonly morningSlotsSelected = computed(() =>
    this.morningSlots().filter(s => this.selectedSlots().includes(s.id)).length
  );
  readonly afternoonSlotsSelected = computed(() =>
    this.afternoonSlots().filter(s => this.selectedSlots().includes(s.id)).length
  );
  readonly groupedSlots = computed(() => this.groupSlots(this.selectedSlots()));

  // ── Step 6: Review Details ──────────────────────────────────
  readonly isSubmitting = signal(false);
  readonly showSuccessPopup = signal(false);

  // ── Assign popup ─────────────────────────────────────────────
  readonly showAssignPopup = signal(false);
  readonly assignPopupType = signal<AssignType>('management');
  readonly assignPopupTitle = signal('');

  // ── Derived ─────────────────────────────────────────────────
  readonly isReviewStep = computed(() => this.currentStep() === 'review_details');

  ngOnInit(): void {
    this.isMobileView.set(window.innerWidth < 768);
    this.loadServiceTypes();

    const addrs: any[] = (this.currentUser as any)?.addresses ?? [];
    if (addrs.length) {
      const a = addrs[0];
      const label = [a.address, a.city, a.state?.short_name ?? '', a.zipcode].filter(Boolean).join(', ');
      this.locationAddress.set(label);
      // Populate lat/long from address data or geocode it
      const lat = Number(a.lat ?? a.latitude);
      const lng = Number(a.lng ?? a.longitude);
      if (lat && lng) {
        this.locationSearchCenter.set({ lat, lng });
      } else if (label) {
        this.gmapService.api.then(() => {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ address: label }, (results: any, status: any) => {
            if (status === 'OK' && results?.[0]?.geometry?.location) {
              this.locationSearchCenter.set({
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
              });
            }
          });
        }).catch(() => {});
      }
    }

  }

  onWindowResize(): void {
    this.isMobileView.set(window.innerWidth < 768);
  }

  onDocClick(e: MouseEvent): void {
    if (this.showHourDropdown() || this.showMinuteDropdown()) {
      if (!(e.target as HTMLElement).closest('.time-dropdown-wrap')) {
        this.showHourDropdown.set(false);
        this.showMinuteDropdown.set(false);
      }
    }
  }

  // ── Navigation ──────────────────────────────────────────────
  private setStep(step: AmenityStep): void {
    this.currentStep.set(step);
    this.cdr.markForCheck();
  }

  onBack(): void {
    const s = this.currentStep();
    if (s === 'review_details') this.setStep('time_slots');
    else if (s === 'time_slots') this.setStep('pickup_datetime');
    else if (s === 'pickup_datetime') {
      this.setStep(this.forSelf() ? 'amenity_type' : 'select_contacts');
    }
    else if (s === 'select_contacts') {
      if (this.returnToReview()) { this.returnToReview.set(false); this.setStep('review_details'); }
      else this.setStep('amenity_type');
    }
    else if (s === 'amenity_type') this.setStep('who_for');
    else this.close.emit();
  }

  onClose(): void {
    this.close.emit();
  }

  // ── Step 1 ───────────────────────────────────────────────────
  selectWhoFor(self: boolean): void {
    this.forSelf.set(self);
    this.setStep('amenity_type');
  }

  // ── Step 2 ───────────────────────────────────────────────────
  loadServiceTypes(): void {
    if (this.serviceTypes().length) return;
    this.isLoadingServiceTypes.set(true);
    this.httpClient.getWithParams<{ success: boolean; data: any[] }>(
      'request/serviceTypeList', { service_type: 'Schedule Amenities' }
    ).subscribe({
      next: res => {
        this.isLoadingServiceTypes.set(false);
        if (res.success && Array.isArray(res.data) && res.data.length) {
          this.serviceTypes.set(res.data[0]?.request_types_details ?? []);
        }
        this.cdr.markForCheck();
      },
      error: () => { this.isLoadingServiceTypes.set(false); this.cdr.markForCheck(); },
    });
  }

  getServiceIcon(type: ServiceType): string {
    return type.url ?? 'assets/icons/fill-icons/default.svg';
  }

  onAmenityTypeClick(type: ServiceType): void {
    this.selectedServiceType.set(type);
    if (this.forSelf()) {
      this.setStep('pickup_datetime');
    } else {
      this.setStep('select_contacts');
    }
  }

  // ── Step 3: Contacts ─────────────────────────────────────────
  openGuestSelector(): void {
    this.returnToReview.set(true);
    this.setStep('select_contacts');
  }

  onGuestListPopupClose(event: any): void {
    if (event.back) {
      this.onBack();
      return;
    }
    if (event.close) {
      const contacts: any[] = event.data?.addedcontacts ?? [];
      this.selectedGuests.set(contacts);
      if (this.returnToReview()) {
        this.returnToReview.set(false);
        this.setStep('review_details');
      } else {
        this.setStep('pickup_datetime');
      }
    }
  }

  onAddGuestClicked(): void {
    this.isAddContactPopupOpen.set(true);
  }

  onAddContactPopupClose(): void {
    this.isAddContactPopupOpen.set(false);
  }

  getContactDisplayName(c: any): string {
    return c?.management_name || c?.service_provider_name || `${c?.first_name ?? ''} ${c?.last_name ?? ''}`.trim() || 'Unknown';
  }

  getContactInitials(c: any): string {
    const name = this.getContactDisplayName(c);
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // ── Step 4: Date & Time ──────────────────────────────────────
  getFormattedDate(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
  }

  getDayOfWeek(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][d.getDay()];
  }

  getHourLabel(): string {
    const h = parseInt(this.selectedHour(), 10);
    const ampm = h < 12 ? 'AM' : 'PM';
    const display = h % 12 || 12;
    return `${String(display).padStart(2, '0')} ${ampm}`;
  }

  openDatePicker(input: HTMLInputElement): void {
    try { input.showPicker(); } catch { input.click(); }
  }

  onDateChange(e: Event): void {
    this.selectedDate.set((e.target as HTMLInputElement).value);
  }

  toggleHourDropdown(e: MouseEvent): void {
    e.stopPropagation();
    this.showHourDropdown.set(!this.showHourDropdown());
    this.showMinuteDropdown.set(false);
  }

  toggleMinuteDropdown(e: MouseEvent): void {
    e.stopPropagation();
    this.showMinuteDropdown.set(!this.showMinuteDropdown());
    this.showHourDropdown.set(false);
  }

  selectHour(i: number): void {
    this.selectedHour.set(this.hoursList[i].value);
    this.showHourDropdown.set(false);
  }

  selectMinute(i: number): void {
    this.selectedMinute.set(this.minutesList[i]);
    this.showMinuteDropdown.set(false);
  }

  onDateTimeNext(): void {
    this.loadSlots();
    this.setStep('time_slots');
  }

  // ── Step 5: Time Slots ───────────────────────────────────────
  loadSlots(): void {
    if (this.slots().length) return;
    this.isLoadingSlots.set(true);
    this.httpClient.get<{ success: boolean; data: Slot[] }>('request/getSlots').subscribe({
      next: res => {
        this.isLoadingSlots.set(false);
        if (res.success && Array.isArray(res.data)) this.slots.set(res.data);
        this.cdr.markForCheck();
      },
      error: () => { this.isLoadingSlots.set(false); this.cdr.markForCheck(); },
    });
  }

  private isSlotMorning(slot: Slot): boolean {
    return slot.isMorning;
  }

  setHoursCount(n: number): void {
    this.selectedHoursCount.set(n);
    const slots = this.selectedSlots();
    if (slots.length > n) {
      this.selectedSlots.set(slots.slice(0, n));
    }
  }

  toggleSlot(slot: Slot): void {
    if (!slot.is_available) return;
    const current = this.selectedSlots();
    if (current.includes(slot.id)) {
      this.selectedSlots.set(current.filter(id => id !== slot.id));
    } else if (current.length < this.selectedHoursCount()) {
      this.selectedSlots.set(
        [...current, slot.id].sort((a, b) => Number(a) - Number(b))
      );
    }
  }

  isSlotSelected(slot: Slot): boolean {
    return this.selectedSlots().includes(slot.id);
  }

  getSlotLabel(slot: Slot): string {
    return slot.time;
  }

  private groupSlots(slotIds: string[]): { label: string }[] {
    if (!slotIds.length) return [];
    const allSlots = this.slots();
    const selected = allSlots
      .filter(s => slotIds.includes(s.id))
      .sort((a, b) => Number(a.id) - Number(b.id));
    if (!selected.length) return [];
    const groups: { start: Slot; end: Slot }[] = [];
    let start = selected[0], prev = selected[0];
    for (let i = 1; i < selected.length; i++) {
      if (Number(selected[i].id) !== Number(prev.id) + 1) {
        groups.push({ start, end: prev });
        start = selected[i];
      }
      prev = selected[i];
    }
    groups.push({ start, end: prev });
    return groups.map(g => {
      const count = Number(g.end.id) - Number(g.start.id) + 1;
      const nextSlot = allSlots.find(s => Number(s.id) === Number(g.end.id) + 1);
      const endLabel = nextSlot?.time ?? '';
      return {
        label: `${g.start.time}${endLabel ? ' to ' + endLabel : ''} for ${count} ${count === 1 ? 'hour' : 'hours'}`,
      };
    });
  }

  onTimeSlotsNext(): void {
    // The backend now derives slots_hour from selected_slots.length rather than a value
    // we send — enforce the counts matching here so a short selection can't silently
    // create a shorter reservation than the Hours the user actually chose.
    if (this.selectedSlots().length !== this.selectedHoursCount()) return;
    this.setStep('review_details');
  }

  // ── Step 6: Review ───────────────────────────────────────────
  getUserFullName(): string {
    const u = this.currentUser;
    return `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim();
  }

  getUserAvatar(): string {
    return this.currentUser?.profile_picture || 'assets/icons/default-profile.png';
  }

  getAssigneeAvatar(item: { profile_picture?: string; documents?: { url?: string }[] } | null | undefined): string | null {
    const url = item?.profile_picture || item?.documents?.[0]?.url;
    return typeof url === 'string' && url.trim() ? url : null;
  }

  isPictureBlocked(status: string | null | undefined): boolean {
    return status === 'flagged' || status === 'blocked';
  }

  getUserAddress(): string {
    if (this.locationAddress()) return this.locationAddress();
    const addrs: any[] = this.currentUser?.addresses;
    if (!addrs?.length) return '';
    const a = addrs[0];
    return [a.address, a.city, a.state?.short_name ?? '', a.zipcode].filter(Boolean).join(', ');
  }

  private getUserApartment(): string {
    const addrs: any[] = (this.currentUser as any)?.addresses ?? [];
    if (!addrs.length) return '';
    const a = addrs[0];
    return a.apartment ?? a.apt ?? a.unit ?? '';
  }

  getPickupDateTimeLabel(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const h = parseInt(this.selectedHour(), 10);
    const ampm = h < 12 ? 'AM' : 'PM';
    const displayH = h % 12 || 12;
    return `${months[d.getMonth()]} ${d.getDate()}, ${days[d.getDay()]} ${displayH}:${this.selectedMinute()}${ampm}`;
  }

  onCreateRequest(): void {
    if (this.isSubmitting()) return;
    if (!this.selectedServiceType()) {
      this.toastr.showError('Please select an amenity type.');
      return;
    }
    if (this.selectedSlots().length !== this.selectedHoursCount()) {
      this.toastr.showError(`Please select ${this.selectedHoursCount()} time slot(s).`);
      return;
    }
    if (!this.forSelf() && !this.selectedGuests().length) {
      this.toastr.showError('Please select a guest.');
      return;
    }
    this.isSubmitting.set(true);

    const u = this.currentUser;
    const location = this.getUserAddress();
    const center = this.locationSearchCenter();
    const latLong = center ? `${center.lat},${center.lng}` : '';

    const payload: Record<string, unknown> = {
      service_type: 'schedule_amenities',
      request_type: this.selectedServiceType()?.key || this.selectedServiceType()?.name?.toLowerCase().replace(/\s+/g, '_') || '',
      option: this.forSelf() ? 'for_self' : 'with_others',
      ...(!this.forSelf() && { guest_members: this.selectedGuests().map(g => String(g.user_id)) }),
      pickup_date: this.selectedDate(),
      pickup_time: `${this.selectedHour()}:${this.selectedMinute()}:00`,
      // Backend derives slots_hour from selected_slots.length automatically — no need to send it.
      selected_slots: this.selectedSlots(),
      pickup_location: location,
      dropoff_location: location,
      pickup_location_lat_long: latLong,
      dropoff_location_lat_long: latLong,
      apartment: this.getUserApartment(),
      tenant_id: String(u?.user_id ?? ''),
    };
    if (this.selectedManagement()) {
      payload['management_id'] = String(this.selectedManagement()!.user_id);
    }
    if (this.selectedFrontDesk()) {
      payload['contractor_id'] = String(this.selectedFrontDesk()!.user_id);
    }

    this.httpClient.post('request/add', payload).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.activityRefresh.triggerRefresh();
        this.showSuccessPopup.set(true);
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.isSubmitting.set(false);
        this.toastr.showError(err?.error?.message || 'Failed to create request. Please try again.');
        this.cdr.markForCheck();
      },
    });
  }

  onSuccessOk(): void {
    this.showSuccessPopup.set(false);
    this.requestComplete.emit({ success: true });
    this.close.emit();
  }

  // ── Assign popup ─────────────────────────────────────────────
  openAssignPopup(type: AssignType, title: string): void {
    this.assignPopupType.set(type);
    this.assignPopupTitle.set(title);
    this.showAssignPopup.set(true);
  }

  onAssignSelected(item: any): void {
    this.showAssignPopup.set(false);
    if (!item) return;
    if (this.assignPopupType() === 'management') {
      this.selectedManagement.set(item);
    } else if (this.assignPopupType() === 'front_desk') {
      this.selectedFrontDesk.set(item);
    }
  }

  // ── Location dialog ──────────────────────────────────────────
  openLocationDialog(): void {
    this.addressInput.set(this.locationAddress());
    this.autocompleteResults.set([]);
    this.showLocationDialog.set(true);
    this.cdr.markForCheck();
    setTimeout(() => this.initLocationMap(), 300);
  }

  dismissLocationDialog(): void {
    this.teardownLocationMap();
    this.showLocationDialog.set(false);
  }

  private async initLocationMap(): Promise<void> {
    if (this.locationMapInitialized || !this.locationMapEl?.nativeElement) return;
    try {
      await this.gmapService.api;
      const center = this.locationSearchCenter() ?? { lat: 40.7128, lng: -74.006 };
      this.locationMapInstance = new google.maps.Map(this.locationMapEl.nativeElement, {
        center,
        zoom: 13,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        ],
      });
      this.locationAutocompleteService = new google.maps.places.AutocompleteService();
      this.locationMapInitialized = true;
      const address = this.addressInput().trim();
      const sc = this.locationSearchCenter();
      if (sc && address) {
        this.buildLocationPickupMarker(sc, address);
      }
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Location map init failed:', e);
    }
  }

  onAddressInput(value: string): void {
    this.addressInput.set(value);
    if (!value.trim() || !this.locationAutocompleteService) {
      this.autocompleteResults.set([]);
      return;
    }
    this.locationAutocompleteService.getPlacePredictions(
      { input: value, types: ['address'] },
      (predictions: any[] | null) => {
        this.autocompleteResults.set(predictions ?? []);
        this.cdr.markForCheck();
      },
    );
  }

  onSelectAutocomplete(prediction: any): void {
    this.addressInput.set(prediction.description);
    this.autocompleteResults.set([]);
    this.gmapService.api.then(() => {
      const placesService = new google.maps.places.PlacesService(this.locationMapInstance);
      placesService.getDetails(
        { placeId: prediction.place_id, fields: ['geometry'] },
        (place: any, status: string) => {
          if (status === 'OK' && place?.geometry?.location) {
            const loc = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            this.locationSearchCenter.set(loc);
            this.buildLocationPickupMarker(loc, prediction.description);
          } else {
            this.locationSearchCenter.set(null);
          }
          this.cdr.markForCheck();
        },
      );
    });
  }

  onAddressSearch(): void {
    this.autocompleteResults.set([]);
    const searchText = this.addressInput().trim();
    if (!searchText) return;
    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: searchText }, (results: any, status: any) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          const loc = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          };
          this.locationSearchCenter.set(loc);
          this.buildLocationPickupMarker(loc, searchText);
        }
        this.cdr.markForCheck();
      });
    } catch (e) {
      console.error('Location geocoding failed:', e);
    }
  }

  selectSavedAddress(label: string): void {
    this.addressInput.set(label);
    this.autocompleteResults.set([]);
    this.onAddressSearch();
  }

  private buildLocationPickupMarker(loc: { lat: number; lng: number }, address: string): void {
    if (this.locationPickupOverlay) {
      try { this.locationPickupOverlay.setMap(null); } catch { }
      this.locationPickupOverlay = null;
    }
    if (!this.locationMapInstance) return;
    const latlng = new google.maps.LatLng(loc.lat, loc.lng);
    const safeAddress = address
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const el = document.createElement('div');
    el.style.cssText = [
      'position:absolute',
      'transform:translate(-50%, -100%)',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'pointer-events:none',
      'z-index:1000',
    ].join(';');
    el.innerHTML = `
      <div style="background:#ffffff;border-radius:5px;padding:12px 14px;box-shadow:0 4px 20px rgba(0,0,0,0.22);min-width:174px;max-width:220px;">
        <div style="font-size:12px;font-weight:600;color:#17B09A;margin-bottom:5px;">Pickup Location</div>
        <div style="font-size:14px;font-weight:600;color:#192A3E;">${safeAddress}</div>
      </div>
    `;
    const PickupOverlay = class extends google.maps.OverlayView {
      private _el: HTMLDivElement;
      private _latlng: any;
      constructor(overlayEl: HTMLDivElement, latlng: any) {
        super();
        this._el = overlayEl;
        this._latlng = latlng;
      }
      onAdd(): void { this['getPanes']().floatPane.appendChild(this._el); }
      draw(): void {
        const pt = this['getProjection']().fromLatLngToDivPixel(this._latlng);
        if (pt) {
          this._el.style.left = `${pt.x}px`;
          this._el.style.top = `${pt.y}px`;
        }
      }
      onRemove(): void { this._el.parentNode?.removeChild(this._el); }
    };
    this.locationPickupOverlay = new PickupOverlay(el, latlng);
    this.locationPickupOverlay.setMap(this.locationMapInstance);
    this.locationMapInstance.panTo(latlng);
    this.locationMapInstance.setZoom(15);
  }

  private clearLocationMapMarkers(): void {
    this.locationMapMarkers.forEach(m => { try { m.setMap(null); } catch { } });
    this.locationMapMarkers = [];
    if (this.locationPickupOverlay) {
      try { this.locationPickupOverlay.setMap(null); } catch { }
      this.locationPickupOverlay = null;
    }
  }

  private teardownLocationMap(): void {
    this.clearLocationMapMarkers();
    this.locationMapInstance = null;
    this.locationMapInitialized = false;
    this.locationAutocompleteService = null;
  }

  onLocationDialogNext(): void {
    const addr = this.addressInput().trim();
    if (addr) this.locationAddress.set(addr);
    this.teardownLocationMap();
    this.showLocationDialog.set(false);
  }
}
